import React, { createContext, useContext } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { LOGIN_MUTATION } from './LOGIN_MUTATION';
import { G, ROUTES } from 'enums';
import { RouteValue } from 'types';
import { Mutation, MutationLoginArgs } from 'generated/graphql';

interface AuthContext {
  signin: (p: { username: string; password: string }, cb: (route: RouteValue) => void) => void;
  signout: (cb: (route: RouteValue) => void) => void;
  loading: boolean;
  error?: ApolloError;
  token?: string;
}

function useProvideAuth(): Omit<AuthContext, typeof G.token> {
  const [login, { loading, error }] = useMutation<Mutation, MutationLoginArgs['input']>(LOGIN_MUTATION, {
    errorPolicy: 'all',
  });

  const signin: (p: Record<'username' | 'password', string>, cb: (path: RouteValue) => void) => void = async (
    { username, password },
    cb
  ) => {
    try {
      const { data } = await login({
        variables: {
          username,
          password,
        },
      });
      if (data?.login) {
        const token = data.login;
        localStorage.setItem(G.token, token);
        cb(ROUTES.home);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const signout = (cb: (path: RouteValue) => void): void => {
    localStorage.removeItem(G.token);
    cb(ROUTES.login);
  };

  return {
    loading,
    error,
    signin,
    signout,
  };
}

const defaultContext: AuthContext = {
  token: localStorage.getItem(G.token) ?? undefined,
  signin: () => {},
  signout: () => {},
  loading: false,
};

const authContext = createContext(defaultContext) as unknown as React.Context<Omit<AuthContext, typeof G.token>>;

export const ProvideAuth: (p: { children: React.ReactChild }) => JSX.Element = ({ children }) => {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
};

export const useAuth = (): Omit<AuthContext, typeof G.token> => useContext(authContext);
